import { Injectable } from '@angular/core';
import { IntegrationItemServiceInf, StatusString, UserLicense } from '../interfaces/integrations';
import { OrdersService } from './orders.service';
import { AccountsService } from './accounts.service';
import { AppInjector } from '../../app-injector';
import { Account, User } from '@mitel/cloudlink-sdk';
import { AuthService } from './auth.service';
import { AppUserClaimsService } from './app-user-claims.service';
import { SpinnerService } from './spinner.service';

import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { MiteamOptionalLobbyDialogComponent } from '../../modules/integrations-applications/components/integ-config-dialogs/miteam-optional-lobby-dialog/miteam-optional-lobby-dialog.component';
import { AccessPolicy, PolicyPostRequest, Statement } from '@mitel/cloudlink-sdk/admin';
import { AdminService } from './admin.service';
import { FeatureToggleService } from './feature-toggle.service';
import { environment } from '../../../environments/environment';
import { IntegPolicyService } from 'src/app/core/services/integ-policy.service';
import { XLaunchService } from 'src/app/core/services/x-launch.service';
import { StatusType } from '../constants/statusType.enum';

const OptionalLobbyStatement: Statement =  {
	statementId: 'OptionalLobbyMiTeamIntegration',
	action: [
		'*'
	],
	resource: [
		"https://mitel.io/auth/meet/features/optional-lobby"
	],
	effect: 'Allow' as const
}

const DisableMeetingsAccountAccessStatement: Statement =  {
	statementId: "disable-meetings-account-access",
	action: [
		"*"
	],
	resource: [
		"https://mitel.io/auth/mitel-one/features/meet"
	],
	effect: "Deny" as const
}

@Injectable({
	providedIn: 'root'
})

// this service will hide whatever implementation we eventually have for getting the list of integrations
// types. Right now it is static
export class IntegMiteamMeetingsService implements IntegrationItemServiceInf {

	private adminService: AdminService;
	private orderService: OrdersService = undefined;
	private featureToggleService: FeatureToggleService;
	private accountsService: AccountsService = undefined;
	private authService: AuthService = undefined;
	private appClaimsService: AppUserClaimsService = undefined;
	private spinnerService: SpinnerService = undefined;
	public dialog: MatDialog;
	private policyService: IntegPolicyService;
	private xlaunchService: XLaunchService = undefined;

	constructor( ) 
	{
		//do not get the service here as it has not been initialized yet.	
	}

	initServices(): Promise<any>
	{
		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.orderService )
			this.orderService = AppInjector.injector.get(OrdersService);

		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);

		if ( !this.authService )
			this.authService = AppInjector.injector.get(AuthService);

		if ( !this.appClaimsService )
			this.appClaimsService = AppInjector.injector.get(AppUserClaimsService);

		if ( !this.spinnerService )
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);

		if ( !this.featureToggleService )
			this.featureToggleService = AppInjector.injector.get(FeatureToggleService);

		if ( !this.policyService )
			this.policyService = AppInjector.injector.get(IntegPolicyService);

		if ( !this.xlaunchService )
			this.xlaunchService = AppInjector.injector.get(XLaunchService);

		return Promise.resolve();
	}


	canBeSelected(account: Account): Promise<boolean>
	{
		return this.canBeStandalone( account );
	}


	// this product can exist as a stand alone product or
	// as a subproduct of another. When the parent product is part of the
	// account, then this product can not be standalone
	canBeStandalone(account: Account): Promise<boolean>
	{
		this.initServices();

		return this.accountsService.doesAccountHaveAnyOneSpecifiedProduct( account.accountId, ['MitelOne', 'MiVB'] ).then( productExists => {
			if ( productExists == true )
				return false;
			else
			{
				return true;
			}
		})
		.catch( err => {
			console.warn("Fetching tags fails, which can happen but if there are integrations, it should exist", err)
			return true;
		});
	}

	isIntegrationStateChangeable(account: Account): Promise<boolean>
	{
		this.initServices();

		return this.accountsService.doesAccountHaveAnyOneSpecifiedProduct( account.accountId, ['MiVB'] ).then( productExists => {
			if ( productExists == true )
				return false;
			else
				return true;
		})
		.catch(err => {
			console.log("Failed to get the account tags", err);
			return true;
		});
	}

	async isIntegrationStateOn(account: Account): Promise<boolean>
	{
		this.initServices();

		// this would depend on whether this is stand alone and what is the parent
		// this method is not called for for standalone integration and if that changes this will need to be changes
		// was told that Mitel One, Mitel 5000 and MiVB are all integration that should never need to co-exist
		// if that changes there will need to be a way to collate all the meeting integrations instances

		let hasMitelOneIntegration: boolean = false;
		let hasMiVoiceBusinessIntegration: boolean = false;

		// if we get here we need so sort of sub or a message will be displayed
		// need to determine if there are any parent subs
		try
		{
			let tags = await this.accountsService.getAccountTagsById(account.accountId);
			if ( tags && tags.products && tags.products.indexOf( 'MiVB' ) != -1 ) 
			{
				hasMiVoiceBusinessIntegration = true;
			}
			if ( tags && tags.products && tags.products.indexOf( 'MitelOne' ) != -1 ) 
			{
				hasMitelOneIntegration = true;
			}
		}
		catch (e)
		{
			console.log("Error determining type of integration", e);
			// not destructive so we will continue
		}

		if ( hasMitelOneIntegration == true )
		{
			// check the policy here
			return this.getMeetingsAccountAccessConfiguration(account).then( data => {
				if ( data )
					return Promise.resolve(data.allowTelephonyAccess);
				return Promise.resolve(true);
			});
		}
		else if ( hasMiVoiceBusinessIntegration == true )
		{
			// if it does not have the proper sub, it is off
			return this.orderService.doesCustomerAccountHaveExistingProductSubsAndTier(
				environment.subscriptionIds.MiVoiceBusiness, ["MiVB_Premier",	"MiVB_Elite"]).then(existingSubs => {
					if ( existingSubs == true )
						return true;

					// no MiVB subs, lets look for standalone subs
					return this.orderService.doesCustomerAccountHaveExistingProductSubs(environment.subscriptionIds.MiTeam).then(meetSubs => {
						return meetSubs;
					});
			});
		}
		else 
			return Promise.resolve(true); // default on. 
	}

	getConfigurationDialog(setupComplete?: boolean) : any
	{
		this.initServices();
		// if (setupComplete) {
		return MiteamOptionalLobbyDialogComponent;
		// }
		// return undefined;
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		return Promise.resolve(true);

		// Here we will determine if a set up is required
		// this.initServices();

		// return this.canBeStandalone(account).then(standalone => {
		// 	if ( standalone == false )
		// 		return true; // Mitel One exists on the account so no free trial

		// 	return Promise.resolve(true);

			// if ( this.appClaimsService.isAssumedRole() )
			// {
			// 	//set up a free trial only if there is no MiCollab
			// 	return this.accountsService.doesAccountHaveSpecifiedProduct( account.accountId, 'MiCollab' ).then( productExists => {
			// 		if ( productExists == false )
			// 		{
			// 			return this.orderService.isPartnerBillingAccountCreated(account).then( created => {
			// 				if ( created == true )
			// 				{
			// 					return this.orderService.doesCustomerAccountHaveExistingProductSubs(environment.subscriptionIds.MiTeam).then(existingSubs => {

			// 						if ( existingSubs === true )
			// 						{
			// 							console.log("Set up complete: THere are existing subscriptions for this account so no free trial")
			// 							return true; //no free trials for existing accounts
			// 						}
			// 						else
			// 						{
			// 							console.log("Set up not complete: THere are NO existing subscriptions for this account they are eligible for free trial");
			// 							return false;
			// 						}
			// 					});
			// 				}
			// 				else
			// 				{
			// 					console.log("Setup is not complete: No partner billing account. this account is candidate for free trial")
			// 					return false;
			// 				}
			// 			})
			// 		}
			// 		else
			// 		{
			// 			console.log("set up complete: There is MiCollab so no free trial");
			// 			return Promise.resolve(true);
			// 		}
			// 	});
			// }
			// else
			// {
			// 	console.log("set up complete: Not PARTNER role so no set up")
			// 	return Promise.resolve(true);
			// }
		// })
	}

	// keeping this around because I suspect it will come back at some point
	// isEligibleForFreeTrial(account: Account): Promise<boolean>
	// {
	// 	this.initServices();
	// 	// this is a temporary policy check while we are developing MitelOne. Once it is complete, we will remove the test and the free trial
	// 	return this.canBeStandalone(account).then(standalone => {
	// 		if ( standalone == false )
	// 			return false; // Mitel One exists on the account so no free trial
				
	// 		// not valid to assign free lisenses for Customer admins
	// 		if ( this.appClaimsService.isAssumedRole() )
	// 		{
	// 			// a free trial only if there is no MiCollab
	// 			return this.accountsService.doesAccountHaveSpecifiedProduct( account.accountId, 'MiCollab' ).then( productExists => {
	// 				if ( productExists == false )
	// 				{
	// 					console.log("There is no MiCollab");
	// 					// need to determine if the customer account has any existing MiTeam Meetings subscriptions
	// 					return this.orderService.doesCustomerAccountHaveExistingProductSubs(environment.subscriptionIds.MiTeam).then(existingSubs => {
	// 						if ( existingSubs === true )
	// 						{
	// 							console.log("THere are existing subscriptions for this account so no free trial", account)
	// 							return false; // no free trials for this accounts
	// 						}
	// 						else
	// 						{
	// 							console.log("FREE TRIAL eligible for this account", account);
	// 							return true;
	// 						}
	// 					});
	// 				}
	// 				else
	// 				{
	// 					return false; // if MiCollab is present no free trial
	// 				}
	// 			});
	// 		}
	// 		else
	// 		{
	// 			return Promise.resolve(false); // only Partners can assign free trials for this product
	// 		}
	// 	})
	// }

	// keeping this around because I suspect it will come back at some point
	// async grantFreeTrial(account: Account, product: any): Promise<any>
	// {
	// 	this.initServices();

	// 	return this.canBeStandalone(account).then(async standalone => {
	// 		if ( standalone == false )
	// 			return {}; // Mitel One exists on the account so no free trial

	// 		// not valid to assign free lisenses for Customer admins
	// 		if ( this.appClaimsService.isAssumedRole() )
	// 		{
	// 			// we always will show a dialog to request the currency and number of seats
	// 			let billingAccountExists = await this.orderService.hasABillingAccount();
	// 			let partnerClaims = this.appClaimsService.getClaims();
	// 			let dialogData = {
	// 				product: product,
	// 				account: account,
	// 				createBillingAccount: !billingAccountExists,
	// 				subInfo: {
	// 					partnerAccountId: partnerClaims.accountId, 
	// 					productRatePlanChargeId: environment.free_productRatePlanChargeId, 
	// 					productRatePlanId: environment.free_productRatePlanId, 
	// 					price: 0,
	// 					period: 3,
	// 					periodType: "Month",
	// 					eulaUrl: "https://www.mitel.com/-/media/mitel/file/pdf/legal-docs/eula/miteam-meetings-v10-eula-3oct2019.pdf?modified=20191205154622"
	// 				}
	// 			};

	// 			let dialogRef = this.dialog.open(FreeTrialDialogComponent,
	// 			{
	// 				disableClose: true,
	// 				autoFocus: false,
	// 				data: dialogData
	// 			});
	// 			return new Promise<boolean> ( (resolve, reject) => {
	// 				dialogRef.afterClosed().subscribe( async result => {
	// 					console.log("The result of the complete setup dialog", result);
	// 					// check the result and act accordingly
	// 					return resolve(result);
	// 				});
	// 			});
	// 		}
	// 		else
	// 			Promise.resolve({});
	// 	});
	// }


	handleIntegrationStateChange( account: Account, state: boolean , productsPriorChange?: string[]): Promise<any>
	{
		this.initServices();
 
		return this.canBeStandalone(account).then(async standalone => {
			if ( standalone == false ) /// this means it is a subfeature for Mitel One
			{
				this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
				this.spinnerService.showSpinner();
				return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( newAccount => {
					if ( newAccount == undefined || 
						newAccount.tags == undefined ||
						newAccount.tags.products == undefined ||
						newAccount.tags.products.indexOf( 'MitelOne' ) == -1 )
						return; // at this point for non standalone only Mitel One handles this event
						
					if ( state == true )
					{
						// remove the account policy statement
						if ( newAccount['policyId'] === undefined ) // nothing to remove
						{
							this.spinnerService.hideSpinner();
							return Promise.resolve({});
						}
						return this.adminService.getPolicy(account.accountId, newAccount['policyId']).then( policy => {
							console.log("Here are the account policies", policy);
							const foundPolicy = policy.statements.find(stmt => {
								return stmt.statementId === DisableMeetingsAccountAccessStatement.statementId;
							});
							if ( foundPolicy )
							{
								return this.policyService.removeStatementFromPolicy(newAccount, policy, DisableMeetingsAccountAccessStatement).then(ret => {
									this.spinnerService.hideSpinner();
									return ret;
								})
							}
							else
							{
								this.spinnerService.hideSpinner();
								return Promise.resolve({});
							}
						});
		
					}
					else
					{
						// add the policy statement for the account
						return this.policyService.addStatementToPolicy( newAccount, DisableMeetingsAccountAccessStatement).then( policy => {
							if ( newAccount['policyId'] == undefined ) {
								let dataChanges = {
									policyId: policy.policyId,
								}
								return this.adminService.updateAccount( newAccount.accountId, dataChanges).then(result => {
									this.accountsService.mergeAccountWithModel(result);
									this.spinnerService.hideSpinner();
									return policy;
								});
							}
							else
							{
								this.spinnerService.hideSpinner();
								return policy;
							}
						}).catch(err => {
							console.log("Failed to add/update policy", err);
							throw err;
						});
					}
				}).catch(err => {
					console.log("Failed to get the account", err);
					throw err;
				});
			}
			else // this is for a stand alone Meetings integration
			{
				if ( state == false )
				{
					console.log("Here we will check for licenses to determine if licenses should be removed");

					return this.accountsService.getAccountTagsById(account.accountId).then((tags: any) => {
						return this.handleOptionalLobbyPolicyChange(account, false).then(val => {
							if ( tags && tags.products )
							{
								if ( tags.products.indexOf('MiCollab') == -1 && (!productsPriorChange || productsPriorChange.indexOf('MiCollab') == -1)){
									this.orderService.removeAllUsersLicenses(account.accountId, 'MiTeam Meetings'); // there appears to be no way to wait
								}
							}
							else //no products, so remove them
							{
								if(!productsPriorChange || productsPriorChange.indexOf('MiCollab') == -1)
									this.orderService.removeAllUsersLicenses(account.accountId, 'MiTeam Meetings'); // there appears to be no way to wait
							}
							return;
						})
					})
				} 
				else if ( state == true )
				{
					// this is temporary during this transition phase to Mitel One
					// this covers the situation where Mitel One was removed and the 
					// over ride Miteam Meetings policy was on. We can not accurately detect this state
					// during this transition but the condition ends up here. SInce this will cause no harm, we
					// will remove the policy here because it needs to be removed.
					return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( async newAccount => {
						// remove the account policy statement
						if ( newAccount['policyId'] === undefined ) // nothing to remove
						{
							return Promise.resolve({});
						}
						//removes optional lobby policy if MiTeam Meetings if there is no existing Meeting stand alone integration
						if ( newAccount.tags && newAccount.tags.products )
						{
							if ( newAccount.tags.products.indexOf('MiTeam') == -1 ){
								 await this.handleOptionalLobbyPolicyChange(account, false);
							}
						}
						return this.adminService.getPolicy(account.accountId, newAccount['policyId']).then( policy => {
							console.log("Here are the account policies", policy);
							const foundPolicy = policy.statements.find(stmt => {
								return stmt.statementId === DisableMeetingsAccountAccessStatement.statementId;
							});
							if ( foundPolicy )
							{
								return this.policyService.removeStatementFromPolicy(newAccount, policy, DisableMeetingsAccountAccessStatement);
							}
							else
								return Promise.resolve({});
						});
					}).catch(err => {
						console.log("Failed to clear the policy, but it is OK for now", err);
						return Promise.resolve({});
					});
				}
			}
		});
	}
	
	// When the parent is removed, this is the method called so the child can clean up as well
	// remove the existing meetings access policy
	removeAsASubintegration?( account: Account ): Promise<any>
	{
		this.initServices();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();
		return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( newAccount => {
			if ( newAccount == undefined || 
				newAccount.tags == undefined ||
				newAccount.tags.products == undefined ||
				newAccount.tags.products.indexOf( 'MitelOne' ) == -1 )
				return; // at this point for non standalone only Mitel One handles this event
				
			// remove the account policy statement
			if ( newAccount['policyId'] === undefined ) // nothing to remove
			{
				this.spinnerService.hideSpinner();
				return Promise.resolve({});
			}
			return this.adminService.getPolicy(account.accountId, newAccount['policyId']).then( policy => {
				console.log("Here are the account policies", policy);
				const foundPolicy = policy.statements.find(stmt => {
					return stmt.statementId === DisableMeetingsAccountAccessStatement.statementId;
				});
				if ( foundPolicy )
				{
					return this.policyService.removeStatementFromPolicy(newAccount, policy, DisableMeetingsAccountAccessStatement).then(ret => {
						this.spinnerService.hideSpinner();
						return ret;
					})
				}
				else
				{
					this.spinnerService.hideSpinner();
					return Promise.resolve({});
				}
			});
		}).catch(err => {
			// removal is best effort so do not throw an error
			console.log("Failed to get the account", err);
			this.spinnerService.hideSpinner();
		});
	}

	async getStatusString(account: Account): Promise<StatusString>
	{
		this.initServices();
		let status: StatusString = {
			status: undefined, 
			type: StatusType.caution
		}

		let hasMitelOneIntegration: boolean = false;
		let hasMiVoiceBusinessIntegration: boolean = false;

		// if we get here we need so sort of sub or a message will be displayed
		// need to determine if there are any parent subs
		try
		{
			let tags = await this.accountsService.getAccountTagsById(account.accountId);
			if ( tags && tags.products && tags.products.indexOf( 'MiVB' ) != -1 ) 
			{
				hasMiVoiceBusinessIntegration = true;
			}
			if ( tags && tags.products && tags.products.indexOf( 'MitelOne' ) != -1 ) 
			{
				hasMitelOneIntegration = true;
			}
		}
		catch (e)
		{
			console.log("Error determining type of integration", e);
			// not destructive so we will continue
		}

		// for all of these scenarios, if there is a regular meetings sub, that will work
		return this.orderService.doesCustomerAccountHaveExistingProductSubs(environment.subscriptionIds.MiTeam).then(existingSubs => {
			if ( existingSubs === true )
			{
				return undefined;
			}
			else if ( !hasMitelOneIntegration && !hasMiVoiceBusinessIntegration ) // no other integration so show this error
			{
				status.status = 'integrations.miteam-config.no-subscription-standalone';
				return status;
			}
			else if ( hasMitelOneIntegration ) // there is a Mitel One integration so check for Mitel One subs
			{
				if ( this.xlaunchService.isXLaunchedFromBoss() )
				{
					return this.orderService.doesCustomerAccountHaveExistingProductSubs(environment.subscriptionIds.MitelOne).then(existingSubs => {
						if ( existingSubs === true )
						{
							return undefined;
						}
						else
						{
							status.status = 'integrations.miteam-config.no-subscription-mitel-one';
							return status;
						}
					})
					.catch( err => {
						console.log("Could not retrieve subscriptions for the account, assume none", err);
						status.status = 'integrations.miteam-config.no-subscription-mitel-one';
						return status;
					})
				}
				else
				{
					return this.orderService.doesCustomerAccountHaveExistingProductSubsAndTier(
						environment.subscriptionIds.MitelOne, ["MitelOne_Premier",	"MitelOne_Elite"]).then(existingSubs => {
						if ( existingSubs === true )
						{
							return undefined;
						}
						else
						{
							status.status = 'integrations.miteam-config.no-subscription-mitel-one-tier';
							return status;
						}
					})
					.catch( err => {
						console.log("Could not retrieve subscriptions for the account, assume none", err);
						status.status = 'integrations.miteam-config.no-subscription-mitel-one-tier';
						return status;
					})
				}
			}
			else if ( hasMiVoiceBusinessIntegration ) // it has the Mitel Business integration
			{
				return this.orderService.doesCustomerAccountHaveExistingProductSubsAndTier(
					environment.subscriptionIds.MiVoiceBusiness, ["MiVB_Premier",	"MiVB_Elite"]).then(existingSubs => {

					if ( existingSubs === true )
					{
						return undefined;
					}
					else
					{
						status.status = 'integrations.miteam-config.no-subscription-miVoice-business'
						return status;
					}
				})
				.catch( err => {
					console.log("Could not retrieve subscriptions for the account, assume none", err);
					status.status = 'integrations.miteam-config.no-subscription-miVoice-business'
					return status;
				})
			}
		})
		.catch( err => {
			console.log("Could not retrieve subscriptions for the account, assume none", err);
			status.status = 'integrations.miteam-config.no-subscription-standalone'
			return status;
		})
	}

	getConfigButtonLabel(account: Account): Promise<string>
	{
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		this.initServices();
		return this.accountsService.doesAccountHaveSpecifiedProduct( account.accountId, 'MiCollab' ).then( productExists => { 
			if (!productExists) {
				return true;
			} else {
				const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_MEETINGS_CONFIRMATION } });
				return dialogRef.afterClosed().toPromise().then(result => {
					return true;
				});
			}
		});
	}

	getUserLicense( user: User, account: Account ): Promise<UserLicense>
	{
		this.initServices();
		return this.orderService.getUsersLicensesFromCachedList(user.userId).then( licenses => {
			let userLicense: UserLicense = {
				code: "MiTeam Meetings",
				displayStringKey: 'users_list.licenseColumn.MiTeam Meetings'
			}
			for ( var i = 0; i < licenses.length; i++ )
			{
				if ( licenses[i] == "MiTeam Meetings" )
				{
					return userLicense;
				}
			}
			return undefined;
		});
	}
	
	isOptionalLobbyPolicyApplied(account): Promise<any> {
		this.initServices();
		// state of an account can change while being in the edit account page so we are getting account again in case multiple account admin are there.
		return this.adminService.getAccountWithoutFrills(account.accountId).then( async newAccount => {
				if ( newAccount['policyId'] != undefined )
				{
					this.spinnerService.showSpinner();
					this.spinnerService.setSpinnerMessageByKey('spinnerMessages.loading_wait');
					const policyValue = await this.adminService.getPolicy(account.accountId, newAccount['policyId']).then( policy => {
												console.log("Here are the account policies", policy);
												// now find the statment and remove it
												const foundPolicy = policy.statements.find(stmt => {
													return stmt.statementId === "OptionalLobbyMiTeamIntegration";
												});
												if ( foundPolicy )
												{
													return { 'policy': policy, 'found': true, 'account': newAccount};
												}
												else
													return { 'policy': policy, 'found': false, 'account': newAccount };
											}) 
											.catch(err => {
												console.log("Here is the error from getting policies", err)
												return { 'policy': null, 'found': false, 'account': newAccount };
											});
					return policyValue;
				}
				else
					return { 'policy': null, 'found': false, 'account': newAccount };
			}).catch(err => {
				console.log("Failed to get account for OptionalLobbyPolicyApplied method", err);
				return { 'policy': null, 'found': false };
			});
	}

	async handleOptionalLobbyPolicyChange(account, state) {
		const policyValue = await this.isOptionalLobbyPolicyApplied(account);
		if (!state) {
			if ( policyValue.found ) {
				return this.removeOptionalLobbyPolicy(policyValue.account, policyValue.policy);
			} else if (policyValue.policy === null) { 
				// There was an error while retrieving policy or account. So revert the the change.
				return Promise.resolve(null)
			} else {
				// Policy is not present. Do not revert the change.
				return Promise.resolve(true);
			}
		} else {
			return this.addOptionalLobbyPolicy( policyValue.account, policyValue.policy, policyValue.found).then( async policy => {
						if ( policy != null && policyValue.account['policyId'] == undefined) { // only update the account if there was no policy Id exiting
							let dataChanges = {
								policyId: policy.policyId,
							}

							return await this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
								this.accountsService.mergeAccountWithModel(result);
								return policy;
							});
						}
						return policy; 
					}).catch(err => {
						console.log("Failed to add/update policy", err);
						return null;
					});
		}
	}

	removeOptionalLobbyPolicy(account, policy) {
		return this.adminService.removeStatementFromPolicy(
			account.accountId, policy.policyId, "OptionalLobbyMiTeamIntegration").then(data => {
				console.log("this is the returned data", data)
				return data;
			}).catch(err => {
				console.log("Failed to remove policy", err);
				return null;
			})
	}

	addOptionalLobbyPolicy( account: Account, policy: AccessPolicy, found: boolean ): Promise<AccessPolicy>
	{
		if (!account) {
			// error getting account
			return Promise.resolve(null);
		}
		else if ( account['policyId'] != undefined )
		{
			if (!policy) {
				// error getting policy
				return Promise.resolve(null);
			}
			if (found) {
				console.log("We found the policy exists")
				return Promise.resolve(policy);
			}
			else
			{
				return this.updatePolicyWithOptionalLobbyStatement( account.accountId, policy);
			}
		}
		else
			return this.createOptionalLobbyPolicy( account.accountId);
	}

	createOptionalLobbyPolicy(accountId: string): Promise<AccessPolicy>
	{
        const policy: PolicyPostRequest = {
            name: accountId + '-policy',
            statements: [
                OptionalLobbyStatement
            ]
        };
		return this.adminService.createUserPolicy(policy, accountId);
	}

	updatePolicyWithOptionalLobbyStatement( accountId: string, policy: AccessPolicy)
	{
		return this.adminService.updatePolicy( accountId, policy.policyId, null, [OptionalLobbyStatement]);
	}

	getMeetingsAccountAccessConfiguration(account: Account): Promise<any>
	{
		this.initServices();

		let config = {
			allowTelephonyAccess: true
		};

		return this.adminService.getAccountWithoutFrills(account.accountId).then( newAccount => {
			if ( newAccount['policyId'] != undefined )
			{
				return this.adminService.getPolicy(account.accountId, newAccount['policyId']).then( policy => {
					console.log("Here are the account policies", policy);
					const foundPolicy = policy.statements.find(stmt => {
						return stmt.statementId === DisableMeetingsAccountAccessStatement.statementId;
					});
					if ( foundPolicy )
					{
						config.allowTelephonyAccess = false; // the policy being present means it is not allows
					}
					return config;
				}) 
				.catch(err => {
					console.log("Here is the error from getting policies", err)
					throw err;
				});
			}
			else
				return config;
		}).catch(err => {
			console.log("Failed to get account", account,  err);
			throw err;
		});
	}

	canFilterUsersOnProduct?(account: Account): boolean
	{
		return true;
	}

	async filterUsersBasedOnProduct(account: Account, users: User[], filter: any[] ): Promise<User[]>
	{
		this.initServices();
		let newUsersList = new Array<User>();
	
		for ( var i = 0; i < users.length; i++ )
		{
			try
			{
				let licenses = await this.orderService.getUsersLicensesFromCachedList(users[i].userId);
				for ( var j = 0; j < licenses.length; j++ )
				{
					if ( licenses[j] == "MiTeam Meetings" )
					{
						newUsersList.push(users[i]);
					}  
				}
			}
			catch(e)
			{
				console.log("Is there an issue here?", e)
			}
		}
		return newUsersList;
	
	}

	getInfoLink(account: Account): string
	{
		this.initServices();
		if ( this.xlaunchService.isXLaunchedFromBoss() )
		{
			return "integrations.xlaunch-help.miteam-meetings"
		}
		return "integrations.mitel-help.miteam-meetings";
	}
}
